import React, { useState, useEffect } from 'react';
import { Page } from '../types';
import { useAuth } from '../components/context/AuthContext';
import { NotificationType } from '../App';
import LoadingSpinner from '../components/LoadingSpinner';
import PageWrapper from '../components/PageWrapper';

interface MobileDeploymentPageProps {
  navigateTo: (page: Page, anchor?: string) => void;
  showNotification: (message: string, type: NotificationType) => void;
}

interface UserProject {
  id: string;
  name: string;
  type: string;
  created_at: string;
}

const MobileDeploymentPage: React.FC<MobileDeploymentPageProps> = ({ navigateTo, showNotification }) => {
  const { profile, isLoading, getAccessToken } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projects, setProjects] = useState<UserProject[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);

    const [formData, setFormData] = useState({
    projectId: '',
    appName: '',
    bundleId: '',
    category: '',
    description: '',
    keywords: '',
    privacyPolicyUrl: '',
    supportUrl: '',
    deploymentType: '' as 'codemine' | 'byoda' | '',
    platform: '' as 'ios' | 'android' | '',
    appIcon: null as File | null
  });

  // Check if user has mobile deployment subscription
  const hasMobileSubscription = profile?.mobile_deployment_plan && profile.mobile_deployment_plan !== 'none';
  const mobileplan = profile?.mobile_deployment_plan || 'none';

  // Load user's projects
  useEffect(() => {
    const fetchProjects = async () => {
      if (!profile) return;

      try {
        const token = await getAccessToken();
        const workerUrl = (import.meta as any).env?.VITE_WORKER_URL || 'https://codemine-api-worker.shazim-rv11-675.workers.dev';
        const response = await fetch(`${workerUrl}/api/projects`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data: any = await response.json();
          setProjects(data.data?.projects || []);
        }
      } catch (error) {
        console.error('Failed to load projects:', error);
      } finally {
        setLoadingProjects(false);
      }
    };

    fetchProjects();
  }, [profile, getAccessToken]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Auto-generate bundle ID when app name changes
    if (name === 'appName') {
      const cleanName = value.toLowerCase().replace(/[^a-z0-9]+/g, '');
      setFormData(prev => ({
        ...prev,
        appName: value,
        bundleId: `com.codemineapp.${cleanName}`
      }));
    }
  };

  const handleIconUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file
      if (!file.type.startsWith('image/')) {
        showNotification('❌ Please upload an image file', 'error');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        showNotification('❌ App icon must be less than 5MB', 'error');
        return;
      }
      setFormData({ ...formData, appIcon: file });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const accessToken = await getAccessToken();
      const workerUrl = (import.meta as any).env?.VITE_WORKER_URL || 'https://codemine-api-worker.shazim-rv11-675.workers.dev';
      const response = await fetch(`${workerUrl}/api/mobile-deployment/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          userPlan: mobileplan
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error((data as any)?.error || "Submission failed");
      }

            showNotification('✅ Your app has been submitted! Our team will review within 2-5 business days, then submit to the app store (10-12 business days review).', 'success');
      // Reset form
      setFormData({
        projectId: '',
        appName: '',
        bundleId: '',
        category: '',
        description: '',
        keywords: '',
        privacyPolicyUrl: '',
        supportUrl: '',
        deploymentType: '' as 'codemine' | 'byoda' | '',
        platform: '' as 'ios' | 'android' | '',
        appIcon: null
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "An unknown error occurred.";
      showNotification(`❌ ${message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading || loadingProjects) {
    return <div className="pt-28 flex items-center justify-center h-screen"><LoadingSpinner /></div>;
  }

  return (
    <PageWrapper>
      <div className="py-20 sm:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[var(--panel-bg)] backdrop-blur-sm border border-[var(--border-color)] rounded-2xl p-8 md:p-12 max-w-5xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-4xl md:text-5xl font-extrabold text-[var(--text-primary)]">
                📱 Mobile App Deployment
              </h1>
              <p className="mt-4 text-lg text-[var(--text-secondary)]">
                Submit your app for deployment to iOS and Android app stores
              </p>
            </div>

            {/* Subscription Status */}
            {hasMobileSubscription ? (
              <div className="mb-8 p-6 bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/30 rounded-xl">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-[var(--text-primary)] mb-1">
                      Active Subscription
                    </h3>
                    <p className="text-[var(--text-secondary)]">
                      {mobileplan === 'codemine_deploy' && '⚡ Codemine Deploy (PKR7,999/month)'}
                      {mobileplan === 'your_account_deploy' && '🛡️ Your Account Deploy (PKR4,999/month)'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-[var(--text-secondary)]">Status</p>
                    <span className="inline-block px-3 py-1 bg-green-500 text-white text-sm font-semibold rounded-full">
                      ✓ Active
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-8 p-6 bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-xl">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-[var(--text-primary)] mb-1">
                      🔒 Subscription Required
                    </h3>
                    <p className="text-[var(--text-secondary)]">
                      Subscribe to a mobile deployment plan to submit apps for deployment
                    </p>
                  </div>
                  <button
                    onClick={() => navigateTo('pricing')}
                    className="whitespace-nowrap px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[var(--gradient-from)] to-[var(--gradient-to)] rounded-md hover:opacity-90 transition-opacity"
                  >
                    View Mobile Plans
                  </button>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className={`space-y-8 ${!hasMobileSubscription ? 'opacity-60 pointer-events-none' : ''}`}>
              {/* Project Selection */}
              <div className="p-6 border border-[var(--border-color)] rounded-lg">
                <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">Select Project</h2>
                <div>
                  <label htmlFor="projectId" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                    Which project do you want to deploy? <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="projectId"
                    id="projectId"
                    required
                    value={formData.projectId}
                    onChange={handleChange}
                    className="w-full p-3 bg-[var(--ws-input-bg)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-lg focus:ring-2 focus:ring-[var(--gradient-from)] focus:outline-none"
                  >
                    <option value="">-- Select a project --</option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name} ({project.type})
                      </option>
                    ))}
                  </select>
                  {projects.length === 0 && (
                    <p className="mt-2 text-sm text-orange-400">
                      No projects found. Create a project in the workspace first.
                    </p>
                  )}
                </div>
              </div>

              {/* Deployment Settings */}
              <div className="p-6 border border-[var(--border-color)] rounded-lg">
                <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">Deployment Settings</h2>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="deploymentType" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                      Deployment Method <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="deploymentType"
                      id="deploymentType"
                      required
                      value={formData.deploymentType}
                      onChange={handleChange}
                      className="w-full p-3 bg-[var(--ws-input-bg)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-lg focus:ring-2 focus:ring-[var(--gradient-from)] focus:outline-none"
                    >
                      <option value="">-- Select method --</option>
                      {mobileplan === 'codemine_deploy' && (
                        <option value="codemine">Codemine Managed (Under Codemine Accounts)</option>
                      )}
                      {mobileplan === 'your_account_deploy' && (
                        <option value="byoda">Your Developer Accounts (BYODA)</option>
                      )}
                    </select>
                  </div>

                                    <div>
                    <label htmlFor="platform" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                      Target App Store <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="platform"
                      id="platform"
                      required
                      value={formData.platform}
                      onChange={handleChange}
                      className="w-full p-3 bg-[var(--ws-input-bg)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-lg focus:ring-2 focus:ring-[var(--gradient-from)] focus:outline-none"
                    >
                      <option value="">-- Select app store --</option>
                      <option value="ios"> iOS App Store</option>
                      <option value="android">🤖 Google Play Store</option>
                    </select>
                    <p className="mt-2 text-xs text-[var(--text-secondary)]">
                      💡 Choose one store per submission. To publish on both stores, submit twice (one for each platform).
                    </p>
                  </div>
                </div>
              </div>

              {/* App Information */}
              <div className="p-6 border border-[var(--border-color)] rounded-lg">
                <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">App Store Listing</h2>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="appName" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                      App Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="appName"
                      name="appName"
                      required
                      maxLength={30}
                      value={formData.appName}
                      onChange={handleChange}
                      placeholder="My Awesome App"
                      className="w-full p-3 bg-[var(--ws-input-bg)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-lg focus:ring-2 focus:ring-[var(--gradient-from)] focus:outline-none"
                    />
                    <p className="mt-1 text-xs text-[var(--text-secondary)]">Maximum 30 characters</p>
                  </div>

                  <div>
                    <label htmlFor="bundleId" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                      Bundle ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="bundleId"
                      name="bundleId"
                      required
                      value={formData.bundleId}
                      onChange={handleChange}
                      placeholder="com.codemineapp.myapp"
                      className="w-full p-3 bg-[var(--ws-input-bg)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-lg focus:ring-2 focus:ring-[var(--gradient-from)] focus:outline-none"
                    />
                    <p className="mt-1 text-xs text-[var(--text-secondary)]">Auto-generated from app name (editable)</p>
                  </div>

                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                      App Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="category"
                      id="category"
                      required
                      value={formData.category}
                      onChange={handleChange}
                      className="w-full p-3 bg-[var(--ws-input-bg)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-lg focus:ring-2 focus:ring-[var(--gradient-from)] focus:outline-none"
                    >
                      <option value="">-- Select category --</option>
                      <option value="Business">Business</option>
                      <option value="Education">Education</option>
                      <option value="Entertainment">Entertainment</option>
                      <option value="Finance">Finance</option>
                      <option value="Food & Drink">Food & Drink</option>
                      <option value="Games">Games</option>
                      <option value="Health & Fitness">Health & Fitness</option>
                      <option value="Lifestyle">Lifestyle</option>
                      <option value="Productivity">Productivity</option>
                      <option value="Shopping">Shopping</option>
                      <option value="Social Networking">Social Networking</option>
                      <option value="Sports">Sports</option>
                      <option value="Travel">Travel</option>
                      <option value="Utilities">Utilities</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                      App Description <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      rows={5}
                      required
                      maxLength={4000}
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Describe your app's features, benefits, and what makes it unique..."
                      className="w-full p-3 bg-[var(--ws-input-bg)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-lg focus:ring-2 focus:ring-[var(--gradient-from)] focus:outline-none resize-none"
                    ></textarea>
                    <p className="mt-1 text-xs text-[var(--text-secondary)]">
                      {formData.description.length}/4000 characters
                    </p>
                  </div>

                                    <div>
                    <label htmlFor="keywords" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                      Keywords (comma-separated) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="keywords"
                      name="keywords"
                      required
                      value={formData.keywords}
                      onChange={handleChange}
                      placeholder="productivity, task manager, todo, organizer"
                      className="w-full p-3 bg-[var(--ws-input-bg)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-lg focus:ring-2 focus:ring-[var(--gradient-from)] focus:outline-none"
                    />
                    <p className="mt-1 text-xs text-[var(--text-secondary)]">Help users discover your app with relevant keywords</p>
                  </div>

                  <div>
                    <label htmlFor="appIcon" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                      App Icon <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0">
                        {formData.appIcon ? (
                          <div className="relative w-20 h-20 rounded-2xl overflow-hidden border-2 border-green-500 shadow-lg">
                            <img
                              src={URL.createObjectURL(formData.appIcon)}
                              alt="App icon preview"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute top-0 right-0 bg-green-500 text-white text-xs px-1 rounded-bl">✓</div>
                          </div>
                        ) : (
                          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center border-2 border-dashed border-gray-600">
                            <span className="text-3xl">📱</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <input
                          type="file"
                          id="appIcon"
                          name="appIcon"
                          accept="image/png,image/jpeg,image/jpg"
                          onChange={handleIconUpload}
                          required
                          className="hidden"
                        />
                        <label
                          htmlFor="appIcon"
                          className="inline-block px-4 py-2 bg-[var(--gradient-from)] text-white rounded-lg cursor-pointer hover:opacity-90 transition-opacity text-sm font-semibold"
                        >
                          {formData.appIcon ? 'Change Icon' : 'Upload Icon'}
                        </label>
                        <p className="mt-2 text-xs text-[var(--text-secondary)]">
                          📐 Required: 1024x1024px PNG or JPG<br/>
                          💾 Max size: 5MB<br/>
                          {formData.appIcon && (
                            <span className="text-green-400">✓ {formData.appIcon.name} ({(formData.appIcon.size / 1024).toFixed(0)}KB)</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Legal & Support */}
              <div className="p-6 border border-[var(--border-color)] rounded-lg">
                <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">Legal & Support</h2>
                <div className="space-y-6">
                  <div>
                    <label htmlFor="privacyPolicyUrl" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                      Privacy Policy URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      id="privacyPolicyUrl"
                      name="privacyPolicyUrl"
                      required
                      value={formData.privacyPolicyUrl}
                      onChange={handleChange}
                      placeholder="https://yourwebsite.com/privacy"
                      className="w-full p-3 bg-[var(--ws-input-bg)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-lg focus:ring-2 focus:ring-[var(--gradient-from)] focus:outline-none"
                    />
                    <p className="mt-1 text-xs text-[var(--text-secondary)]">Required by Apple & Google</p>
                  </div>

                  <div>
                    <label htmlFor="supportUrl" className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                      Support URL or Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="supportUrl"
                      name="supportUrl"
                      required
                      value={formData.supportUrl}
                      onChange={handleChange}
                      placeholder="https://yourwebsite.com/support or support@yourapp.com"
                      className="w-full p-3 bg-[var(--ws-input-bg)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-lg focus:ring-2 focus:ring-[var(--gradient-from)] focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Important Notice */}
              <div className="p-6 bg-orange-500/10 border border-orange-500/30 rounded-xl">
                <h3 className="text-lg font-bold text-[var(--text-primary)] mb-3">
                  📝 Before You Submit
                </h3>
                                <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
                  <li className="flex items-start gap-2">
                    <span className="text-orange-400 mt-0.5">•</span>
                    <span><strong>Step 1:</strong> Our team reviews your app within <strong>2-5 business days</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-400 mt-0.5">•</span>
                    <span><strong>Step 2:</strong> Apple/Google store review takes <strong>10-12 business days</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-400 mt-0.5">•</span>
                    <span>We'll email you for additional screenshots if needed after initial review</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-400 mt-0.5">•</span>
                    <span>Your app will include Codemine branding (splash screen)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-400 mt-0.5">•</span>
                    <span><strong>Active subscription required</strong> - Canceling may result in app removal</span>
                  </li>
                </ul>
              </div>

              {/* Submit Button */}
              <div className="text-center pt-6">
                <button
                  type="submit"
                  disabled={!hasMobileSubscription || isSubmitting || projects.length === 0}
                  className="w-full max-w-xs px-8 py-4 text-lg font-semibold text-white bg-gradient-to-r from-[var(--gradient-from)] to-[var(--gradient-to)] rounded-md hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto"
                >
                  {!hasMobileSubscription ? (
                    '🔒 Subscription Required'
                  ) : isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </>
                  ) : (
                    '🚀 Submit for Deployment'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default MobileDeploymentPage;
